import { CURRENCY_MAX_MAGNITUDE } from '../currency';
import type { Rarity } from '../rarity';
import { EFFECT_MODES, type EffectMode } from './effect-modes';

/**
 * O catálogo de joias, em RASCUNHO (gate V28B).
 *
 * ## Duas naturezas, declaradas — não uma com exceções
 *
 * A arte decidiu isto antes da implementação, e a decisão foi medida em vez de
 * assumida: catorze masters enchem 77,7%–78,8% da sua caixa alpha com aspeto
 * ~1,00, e um disco perfeito enche π/4 = 78,54%. São esferas. O décimo quinto
 * enche 51,3% com aspeto 0,738 — não é. Ver
 * `docs/adr/0049-jewels-have-two-natures.md`.
 *
 * - `socketed` — as catorze esferas. Ocupam um socket de uma cópia de skin, e
 *   **só elas** podem aparecer em `skin_socket_occupancy`.
 * - `sovereign` — o *Coração Primordial*. **Incorporável**: não ocupa socket,
 *   não tem nível e não tem upgrade. O gate V28F.A mudou o que `sovereign`
 *   significa: era "passiva por posse", e a peça concedia porque a conta a
 *   tinha; passou a ser **gasta numa nave**, e é a NAVE que conta quantos
 *   corações tem. Um coração parado no inventário concede zero.
 *
 * Forçar a peça soberana na tabela de ocupação — com um índice fictício ou uma
 * skin fantasma — seria uma mentira que todas as leituras que derivam efeitos
 * teriam de contornar. O modo é um campo da DEFINIÇÃO, e o resto do sistema lê
 * esse campo em vez de perguntar pelo id.
 *
 * ## Rascunho quer dizer rascunho, para TREZE das catorze
 *
 * Treze das `socketed` não concedem NADA. Os seus nomes de ficheiro descrevem
 * intenção de conteúdo — míssil, serra, roubo de vida, reflexão, corte, bomba,
 * sangramento, veneno — e nenhuma dessas mecânicas está desenhada, dimensionada
 * ou implementada. São *procs*, com alvo, momento, duração e interação entre
 * si; o resolvedor de passivos que existe hoje soma atributos e não as
 * suportaria. Inventar aqui um coeficiente para cada uma seria decidir o
 * equilíbrio do jogo num ficheiro de tipos.
 *
 * A décima quarta é a **Chama da Destruição** (`jewel_damage`), ligada no gate
 * V28G.B. Ela é a mais simples das quinze mecânicas do contrato V28D.2 - uma
 * percentagem somada ao tiro normal, sem gatilho, sem duração e sem alvo - e é
 * por isso que foi a primeira: liga-se sem inventar nenhum sistema novo, na
 * mesma parcela somada onde o Coração Primordial já entra.
 *
 * O outro efeito é o da peça soberana: +1% de dano por coração incorporado,
 * exatamente, em basis points inteiros.
 *
 * ## Nomes
 *
 * Só a peça soberana tem nome de exibição, porque foi o único decidido. Os
 * catorze ficam sem nome deliberadamente: um nome inventado é conteúdo, e este
 * ficheiro não decide conteúdo.
 */

/** Como uma joia se liga a um jogador. Permanente, e lido em vez do id. */
export const JEWEL_SOCKET_MODES = ['socketed', 'sovereign'] as const;
export type JewelSocketMode = (typeof JEWEL_SOCKET_MODES)[number];

/**
 * Que FORMA a arte tem, que é uma pergunta diferente de como ela se liga.
 *
 * - `orb` — a silhueta é um disco. Uma borda animada pode ser um círculo comum,
 *   desenhado numa camada própria e nunca derivado de pixel nenhum do master.
 * - `silhouette` — a silhueta não é um disco, e um aro circular cortar-lhe-ia
 *   partes. A borda tem de sair de uma máscara da própria alpha.
 *
 * As duas dimensões coincidem hoje - as catorze de socket são orbes e a
 * soberana é silhueta - e são declaradas em separado porque não são a mesma
 * coisa: uma diz onde a joia se encaixa, a outra diz como se desenha à volta
 * dela. Uma futura joia de socket com forma própria não obrigaria a reescrever
 * o significado de `socketMode`.
 */
export const JEWEL_VISUAL_SHAPES = ['orb', 'silhouette'] as const;
export type JewelVisualShape = (typeof JEWEL_VISUAL_SHAPES)[number];

/**
 * Os dois modos onde uma joia pode produzir efeito (gate V28G.O.1).
 *
 * A CAÇA e o DUELO. Não é a lista de modos que o jogo tem - `PVP_MODES` é sobre
 * recompensa e `PvpRuleset` é sobre quantas cadeiras - é a lista de sítios onde
 * uma mecânica de joia pode acontecer.
 *
 * ## Promovido para `effect-modes.ts` no gate V28H.B
 *
 * A pergunta nunca foi sobre joias, e as cartas fazem exatamente a mesma. Isto
 * continua a ser exatamente aquela lista e exatamente aquele tipo - o nome fica
 * porque é o que dezenas de sítios já leem, e mudá-lo seria uma renomeação
 * enorme a fingir de decisão.
 */
export const JEWEL_EFFECT_MODES = EFFECT_MODES;
export type JewelEffectMode = EffectMode;

/**
 * O efeito que uma joia concede, em unidades inteiras.
 *
 * Vazio para as catorze de socket, e não é um descuido: é o estado em que o
 * conteúdo está. Um campo opcional ausente diz "ainda não decidido"; um zero
 * diria "decidido que é nada", que é diferente e falso.
 */
export interface JewelEffects {
  /**
   * Percentagem de dano por UNIDADE da peça, em basis points. 100 = +1%.
   *
   * Só a soberana o tem, e a unidade dela é o CORAÇÃO INCORPORADO: cada um vale
   * o mesmo, e o que acumula é a contagem na nave.
   */
  readonly damageBps?: number;
  /**
   * Percentagem de dano por NÍVEL SOMADO, em basis points (gate V28G.B).
   *
   * ## Porque é um campo diferente de `damageBps`
   *
   * Porque a unidade é outra, e confundi-las seria um erro que se propaga em
   * silêncio. A peça soberana vale o mesmo por coração e não tem nível; uma joia
   * de socket vale `coef x levelSum`, onde `levelSum` é a soma dos níveis das
   * cópias encaixadas - que é exatamente o `L x n` da lei 3 do contrato V28D.2.
   *
   * Dois campos com o mesmo nome e significados diferentes seriam um sítio onde
   * um resolvedor futuro pode ler o errado.
   */
  readonly damagePerLevelSumBps?: number;
  /**
   * Percentagem de CADÊNCIA por nível somado, em basis points (gate V28G.D).
   *
   * ## Porque não reutiliza `damagePerLevelSumBps`
   *
   * Porque a grandeza é outra. As duas valem 30 bps por nível e produzem o
   * mesmo poder de combate - dano e cadência são equivalentes para DPS, e a
   * paridade da tabela do contrato V28D.2 é deliberada - mas entram em sítios
   * diferentes: uma soma-se à percentagem do tiro, a outra encurta o intervalo
   * entre salvas. Um campo partilhado faria um resolvedor futuro aplicar a
   * mesma parcela duas vezes sem que nada o assinalasse.
   */
  readonly attackSpeedPerLevelSumBps?: number;
  /**
   * Percentagem do DANO APLICADO devolvida em vida, por nível somado, em basis
   * points (gate V28G.I).
   *
   * ## Porque é um terceiro campo e não um dos dois de cima
   *
   * Porque a grandeza é outra, e a diferença é a que o ADR 0054 existe para
   * fixar. As três valem 30 bps por nível — dano, cadência e cura são as três
   * maneiras de gastar o mesmo orçamento de 30 bps de poder de combate — mas
   * entram em sítios que não se tocam: uma soma-se à percentagem do tiro, outra
   * encurta o intervalo entre salvas, e esta multiplica o que um golpe REMOVEU
   * para decidir o que devolver a quem o deu.
   *
   * Um campo partilhado faria um resolvedor futuro somar cura ao dano.
   *
   * ## Porque a base é o dano aplicado
   *
   * Decisão do dono, que revoga a D-4.3 do V28D.2. Um tiro de 40 pontos num
   * casco com 3 de vida aplica 3, e a cura sai de 3 — o overkill não cura,
   * porque nunca chega a ser dano.
   */
  readonly lifestealPerLevelSumBps?: number;
  /**
   * O teto ABSOLUTO desta joia, em basis points do dano aplicado (gate V28G.I).
   *
   * Declarado por joia e não como constante do resolvedor: uma segunda joia de
   * cura contínua — o Coração Vital — terá o seu, e um teto partilhado seria
   * uma decisão de equilíbrio a ser tomada por acidente no dia em que ela
   * chegasse.
   *
   * Vinculativo: acima do nível que lá chega, a joia entrega MENOS do que o
   * orçamento linear. Um teto que acompanhasse a soma não seria um teto.
   */
  readonly lifestealMaxBps?: number;
  /**
   * Percentagem de todo o dano hostil RECEBIDO que deixa de acontecer, por
   * nível somado, em basis points (gate V28G.Q).
   *
   * ## Porque é um quinto campo e não um dos quatro de cima
   *
   * Porque a grandeza é a única do catálogo que se aplica ao golpe de OUTRA
   * pessoa. As quatro anteriores descrevem o que este casco entrega - quanto o
   * tiro dele vale, com que frequência ele sai, e que fração do que ele removeu
   * volta em vida - e todas multiplicam ou somam do lado de quem ataca.
   *
   * Esta multiplica do lado de quem DEFENDE, e o número que ela produz não é
   * somado a nada: ele é o complemento de um multiplicador,
   * `10 000 - reducao`, aplicado ao payload que ia acontecer. Somá-la a
   * `damagePerLevelSumBps` por partilharem um campo faria uma nave reduzir o
   * dano dela própria.
   *
   * ## A lei, e ela é uma linha
   *
   *     reducao(nivel) = 200 x min(nivel, 30)
   *
   * Sem degraus, sem tabela de exceções e sem caso especial. As nove linhas do
   * contrato saem desta multiplicação, e a décima - o nível 31 - sai do mesmo
   * `min` que o resolvedor já aplica a todas as joias.
   */
  readonly damageReductionPerLevelSumBps?: number;
  /**
   * O teto ABSOLUTO desta joia, em basis points do dano recebido (gate V28G.Q).
   *
   * Declarado por joia, como o do Orbe Vampírico e pela mesma razão: uma segunda
   * joia de redução teria o seu, e um teto partilhado seria uma decisão de
   * equilíbrio tomada por acidente no dia em que ela chegasse.
   *
   * ## Redundante com o teto de nível, e escrito na mesma
   *
   * `200 x 30` são exatamente 6 000, portanto o teto de nível já o produz e este
   * número nunca morde num perfil legal. Ele existe para a aritmética ter uma
   * garantia que NÃO depende de `JEWEL_LEVEL_MAX`: o multiplicador resultante é
   * `>= 4 000` por construção, e nenhuma alteração futura do teto de nível pode
   * fazer um golpe hostil chegar a zero - ou a um número negativo - sem que
   * alguém venha mexer nesta linha de propósito.
   */
  readonly damageReductionMaxBps?: number;
  /**
   * Percentagem de VIDA MÁXIMA por nível somado, em basis points (gate V28G.R).
   *
   * ## Porque é um sétimo campo e não um dos seis de cima
   *
   * Porque a grandeza não toca em nenhuma delas. As quatro ofensivas somam-se ao
   * tiro, encurtam o intervalo entre salvas ou multiplicam o que um golpe
   * removeu; a Égide multiplica um golpe que chega. Esta não descreve golpe
   * nenhum: ela é uma parcela somada às percentagens que formam o TETO de um
   * casco, aplicada uma vez sobre a vida plana acumulada antes delas.
   *
   * Um campo partilhado com `damagePerLevelSumBps` faria um resolvedor futuro
   * pagar vida como dano.
   *
   * ## A lei, e ela é uma linha
   *
   *     bonus(nivel) = 200 x min(nivel, 30)
   *
   * Sem degraus, sem tabela de exceções e sem caso especial. As nove linhas do
   * contrato saem desta multiplicação, e a décima - o nível 31 - sai do mesmo
   * `min` que o resolvedor já aplica a todas as joias.
   */
  readonly maxHealthPerLevelSumBps?: number;
  /**
   * O teto ABSOLUTO desta joia, em basis points de vida máxima (gate V28G.R).
   *
   * Declarado por joia, como o do Orbe Vampírico e o da Égide, e pela mesma
   * razão: uma segunda joia de vida teria o seu, e um teto partilhado seria uma
   * decisão de equilíbrio tomada por acidente no dia em que ela chegasse.
   *
   * Ele limita o que ESTA peça concede, e não a vida de uma nave: pontos,
   * artefactos e equipamento continuam a somar-se por cima sem que nada aqui os
   * veja.
   */
  readonly maxHealthMaxBps?: number;
  /**
   * Percentagem de VELOCIDADE DE MOVIMENTO por nível somado, em basis points
   * (gate V28G.T).
   *
   * ## Porque é um nono campo e não um dos oito de cima
   *
   * Porque a grandeza não toca em nenhuma delas. As quatro ofensivas somam-se ao
   * tiro, encurtam o intervalo entre salvas ou multiplicam o que um golpe
   * removeu; a Égide multiplica um golpe que chega; o Coração forma o teto do
   * casco. Esta descreve o DESLOCAMENTO VOLUNTÁRIO de uma nave, que é a única
   * grandeza do catálogo que não fala de dano nem de vida.
   *
   * Um campo partilhado com `attackSpeedPerLevelSumBps` faria um resolvedor
   * futuro pagar movimento como cadência - e as duas são percentagens de uma
   * "velocidade", que é exatamente a confusão que dois campos evitam.
   *
   * ## A lei, e ela é uma linha
   *
   *     bonus(nivel) = 130 x min(nivel, 30)
   *
   * Sem degraus, sem tabela de exceções e sem caso especial. As seis linhas da
   * tabela aprovada saem desta multiplicação, e a do nível 31 sai do mesmo
   * `min` que o resolvedor já aplica a todas as joias.
   */
  readonly moveSpeedPerLevelSumBps?: number;
  /**
   * O teto ABSOLUTO desta joia, em basis points de velocidade (gate V28G.T).
   *
   * Declarado por joia, como o do Orbe Vampírico, o da Égide Esmeralda e o do
   * Coração do Titã, e pela mesma razão: uma segunda joia de movimento teria o
   * seu, e um teto partilhado seria uma decisão de equilíbrio tomada por acidente
   * no dia em que ela chegasse.
   *
   * ## Redundante com o teto de nível, e escrito na mesma
   *
   * `130 x 30` são exatamente 3 900, portanto o teto de nível já o produz e este
   * número nunca morde num perfil legal. Ele existe para o **+39 % ser absoluto
   * por aritmética** e não por confiança: um perfil adulterado, uma instantânea
   * antiga ou um `levelSum` acima de 30 encontram os dois cortes, e nenhuma
   * alteração futura de {@link JEWEL_LEVEL_MAX} pode levar uma nave além dele sem
   * que alguém venha mexer nesta linha de propósito.
   */
  readonly moveSpeedMaxBps?: number;
}

export interface JewelDefinition {
  readonly definitionId: string;
  readonly socketMode: JewelSocketMode;
  /**
   * A forma da arte, DECLARADA aqui e VERIFICADA contra os pixels.
   *
   * Não é derivada do nome do ficheiro, e não é medida em runtime: este pacote
   * é neutro de plataforma e não lê disco. É declarada, e
   * `packages/asset-pipeline` mede a caixa alpha de cada master e falha se a
   * declaração não corresponder - um disco enche π/4 = 78,54% da sua caixa, e
   * a peça soberana enche 51,3%. A declaração é conveniente; a prova é o que a
   * torna verdadeira.
   */
  readonly visualShape: JewelVisualShape;
  /** O ficheiro master, sob `assets-source/jewels/`. */
  readonly master: string;
  /** Só a soberana o tem. Os catorze estão por decidir. */
  readonly displayName: string | null;
  /** Vazio enquanto o conteúdo não estiver decidido. */
  readonly effects: JewelEffects;
  /**
   * **Onde o efeito desta joia FUNCIONA** (gate V28G.O.1).
   *
   * ## O que este campo é, e o que ele nunca é
   *
   * É "onde a mecânica acontece", e **nunca** "onde a peça pode ser equipada".
   * Uma joia exclusivamente de duelo continua a poder ocupar um socket, a
   * aparecer no vestiário e a viajar no perfil congelado de uma caçada - o que
   * ela não faz é produzir efeito lá. Confundir as duas coisas transformaria um
   * aviso honesto numa recusa que ninguém escreveu.
   *
   * ## Porque é uma LISTA e não um enum de um valor
   *
   * Porque a resposta tem três formas e um enum só teria duas. Uma joia pode
   * valer nos dois modos, num só, ou - hoje, para quatro delas - em nenhum:
   * `[]` é o estado de uma peça cuja mecânica não existe, e é diferente de
   * "exclusiva de um modo". Um vazio diz "não produz efeito em lado nenhum", e é
   * a verdade que impede a interface de anunciar exclusividade de duelo a uma
   * peça que também não faz nada no duelo.
   *
   * ## Cada linha foi lida no CÓDIGO, e não na documentação
   *
   * O que decide é por onde o efeito passa: `resolveJewelEffects` para as
   * passivas somadas, os dois registos de loteria para os procs, os ficheiros
   * próprios do Veneno, do Sangramento e da Reflexão, e `primordialHeartBps`
   * para a soberana. Uma joia vale num modo quando a simulação DESSE modo
   * importa o caminho que a produz - e é essa a leitura que
   * `jewel-modes.test.ts` refaz a cada corrida, contra os módulos reais.
   */
  readonly supportedModes: readonly JewelEffectMode[];
  readonly rarity: Rarity;
}

/** O id da peça soberana, nomeado uma vez e lido em todo o lado. */
export const SOVEREIGN_JEWEL_ID = 'jewel_primordial';

/**
 * +1% de dano por CORAÇÃO INCORPORADO, exatamente.
 *
 * Cem basis points inteiros, na mesma unidade que os artefactos já usam, e
 * somados na mesma percentagem única que `hunt/passive-attributes.ts` aplica
 * uma vez.
 *
 * ## O significado mudou no gate V28F.A, e o campo sobreviveu
 *
 * Era "o efeito que a POSSE concede": a conta tinha a peça ou não tinha, uma
 * por conta por índice único. Passou a ser "o efeito que UMA incorporação
 * concede": o coração é gasto numa nave, a nave conta quantos tem, e a contagem
 * acumula sem tecto de jogo. Um coração parado no inventário concede **zero** —
 * ver `docs/gates/v28d2-primordial-ownership.md`.
 *
 * O número não mudou, e é por isso que o campo não mudou de nome: o que mudou é
 * quantas vezes ele conta.
 */
export const SOVEREIGN_JEWEL_DAMAGE_BPS = 100;

/** O id da Chama da Destruição, nomeado uma vez (gate V28G.B). */
export const DAMAGE_JEWEL_ID = 'jewel_damage';

/**
 * +30 bps de dano por NÍVEL SOMADO, para a Chama da Destruição.
 *
 * ## De onde vem este número
 *
 * De `docs/gates/v28d2-jewel-math.json`, que o calcula a partir do orçamento
 * declarado - **um socket vale 30 bps de poder de combate por nível** - e o
 * verifica contra a banda `900 ± 250` para as três colunas da tabela. Não é um
 * valor escolhido aqui: é o valor que o gerador produz, transcrito.
 *
 * As três consequências, que são o contrato:
 *
 * - nível 1 — `30` bps = **+0,30 %**;
 * - nível 30 — `900` bps = **+9 %**, e é o teto final.
 *
 * Não há terceira linha. Uma skin tem UMA joia de cada tipo (gate V28G.K), o
 * nível para nos 30, e o `levelSum` 180 que esta tabela mostrava descrevia seis
 * cópias iguais - uma configuração de combate que deixou de poder existir.
 *
 * ## Porque é por nível
 *
 * Porque a lei 3 do contrato é `coef(L, n) = coef(1) x L x n`, e com uma joia
 * de cada tipo o `n` é 1: o coeficiente é linear no NÍVEL, e mais nada. O campo
 * ainda se chama `levelSum` porque renomeá-lo custaria uma versão nova a cada
 * documento que o carrega, mas o que ele carrega é o nível.
 *
 * Especializar deixou de ser sequer exprimível. Seis sockets são seis joias
 * DIFERENTES, e a escolha do jogador é quais - nunca quantas da mesma.
 *
 * ## O que ele NÃO multiplica
 *
 * Nada além do dano-base autoritativo da nave. Não toca em pets, DoT, reflexão
 * nem em procs de outra joia, e não é multiplicado pela runa de Dano Dobrado -
 * essa continua a multiplicar o projétil FINAL. A separação está imposta pela
 * aritmética em `damage/exact.ts` e provada em `damage/jewel-effects.test.ts`.
 */
export const JEWEL_DAMAGE_PER_LEVEL_BPS = 30;

/** O id do Ciclone de Lâminas, nomeado uma vez (gate V28G.F). */
export const BLADE_CUT_JEWEL_ID = 'jewel_blade_cut';

/** O id do Núcleo Atômico, nomeado uma vez (gate V28G.J). */
export const ATOMIC_BOMB_JEWEL_ID = 'jewel_atomic_bomb';

/**
 * O id do Arsenal Ígneo, nomeado uma vez (gate V28G.L).
 *
 * A joia do MÍSSIL. Como o Núcleo Atômico, ela não declara nada em
 * {@link JewelEffects}: um proc não é um atributo somado, e a sua chance vive no
 * registo da loteria por salva (`damage/volley-lottery.ts`), que é também onde a
 * ordem canónica dos intervalos disjuntos vive. Um campo aqui seria uma segunda
 * casa para a mesma decisão, e a que envelhece é sempre a que ninguém lê.
 */
export const MISSILE_JEWEL_ID = 'jewel_missile';

/**
 * O id do Orbe da Víbora, nomeado uma vez (gate V28G.M).
 *
 * A joia do VENENO. Como o Núcleo Atômico e o Arsenal Ígneo, ela não declara
 * nada em {@link JewelEffects}: um stack de veneno é ESTADO no inimigo, não um
 * atributo somado, e a sua chance vive no registo do seu próprio sorteio
 * (`damage/poison.ts`). Um campo aqui seria uma segunda casa para a mesma
 * decisão, e a que envelhece é sempre a que ninguém lê.
 */
export const POISON_JEWEL_ID = 'jewel_poison';

/**
 * O id da Garra Hemorrágica, nomeado uma vez (gate V28G.N).
 *
 * A joia do SANGRAMENTO. Como o Núcleo Atômico, o Arsenal Ígneo e o Orbe da
 * Víbora, ela não declara nada em {@link JewelEffects}: uma marca de sangramento
 * é ESTADO no inimigo à espera de uma ordem, não um atributo somado, e a sua
 * chance vive no registo do seu próprio sorteio (`damage/bleeding.ts`). Um campo
 * aqui seria uma segunda casa para a mesma decisão, e a que envelhece é sempre a
 * que ninguém lê.
 */
export const BLEEDING_JEWEL_ID = 'jewel_bleeding';

/**
 * O id da Égide Espinhosa, nomeada uma vez (gate V28G.O).
 *
 * A joia da REFLEXÃO. Como o Núcleo Atômico, o Arsenal Ígneo, o Orbe da Víbora e
 * a Garra Hemorrágica, ela não declara nada em {@link JewelEffects}: uma
 * reflexão não é um atributo somado, e não é sequer um proc ofensivo - é uma
 * rolagem DEFENSIVA, feita sobre um payload que já vinha a caminho, e a sua
 * chance vive no registo do seu próprio sorteio (`damage/reflection.ts`), ao
 * lado da tabela fechada que diz o que pode e o que não pode ser intercetado.
 *
 * Um campo aqui seria uma segunda casa para a mesma decisão, e a que envelhece é
 * sempre a que ninguém lê.
 */
export const DAMAGE_REFLECTION_JEWEL_ID = 'jewel_damage_reflection';

/**
 * O id do Diamante Serrilhado, nomeado uma vez (gate V28G.P).
 *
 * A joia das SERRAS ORBITAIS. Como o Núcleo Atômico, o Arsenal Ígneo, o Orbe da
 * Víbora, a Garra Hemorrágica e a Égide Espinhosa, ela não declara nada em
 * {@link JewelEffects} - e a razão dela é a mais forte das seis: as outras cinco
 * não declaram porque uma CHANCE não é um atributo somado, e esta não declara
 * porque **não tem chance nenhuma**.
 *
 * Uma serra é uma coisa que existe enquanto a nave existir. Não espera por um
 * acerto, não substitui um tiro, não sorteia e não consome entropia. A
 * quantidade, a velocidade, o intervalo de contacto e o dano de cada contacto
 * saem todos do nível por aritmética fechada em `damage/saw.ts`, que é onde a
 * lei desta joia vive inteira.
 *
 * Um campo aqui seria uma segunda casa para a mesma decisão, e a que envelhece é
 * sempre a que ninguém lê.
 */
export const SAW_JEWEL_ID = 'jewel_saw';

/**
 * O id da Égide Esmeralda, nomeada uma vez (gate V28G.Q).
 *
 * A joia da REDUÇÃO DE DANO. Ao contrário das seis mecânicas que a precederam,
 * ela DECLARA os seus coeficientes em {@link JewelEffects} - e a razão é a mesma
 * que lá põe a Chama da Destruição, o Pulso do Dragão e o Orbe Vampírico: ela
 * não sorteia nada.
 *
 * Não tem chance, não tem proc, não tem cooldown, não consome entropia, não cria
 * entidade, não aplica estado e não escolhe um instante. Vale um NÚMERO em cada
 * leitura, e um número por nível é exatamente o que o catálogo guarda.
 *
 * O que ela mede é o outro lado do golpe: as três passivas anteriores dizem
 * quanto um casco ENTREGA, e esta diz quanto de um golpe chega a acontecer-lhe.
 */
export const DAMAGE_REDUCTION_JEWEL_ID = 'jewel_damage_reduction';

/**
 * **2 % de todo o dano hostil recebido, por nível somado.** Decisão do dono.
 *
 * A tabela inteira do gate V28G.Q sai desta constante e de {@link JEWEL_LEVEL_MAX},
 * sem uma única linha escrita à mão:
 *
 * | nível | redução | dano recebido |
 * | ---: | ---: | ---: |
 * | 0 | 0 % | 100 % |
 * | 1 | 2 % | 98 % |
 * | 5 | 10 % | 90 % |
 * | 10 | 20 % | 80 % |
 * | 15 | 30 % | 70 % |
 * | 20 | 40 % | 60 % |
 * | 25 | 50 % | 50 % |
 * | 30 | **60 %** | **40 %** |
 * | 31+ | 60 % | 40 % |
 *
 * ## Porque 200 e não 30, como as outras três passivas
 *
 * Porque a unidade é outra e a comparação não se faz. As três de 30 bps gastam
 * o orçamento em PODER OFENSIVO - dano, cadência, sifão - e as três são
 * lineares no que produzem. Esta gasta em SOBREVIVÊNCIA, e a sobrevivência não
 * é linear no que ela dá: 60 % de redução não faz um casco durar 60 % mais
 * tempo, faz durar `1 / 0,4` = **duas vezes e meia** mais. O coeficiente
 * nominal é maior porque a grandeza que ele compra vale menos por ponto.
 *
 * ## E porque não há linha abaixo do 30
 *
 * Uma skin tem UMA joia de cada tipo (gate V28G.K) e o nível para nos 30. O
 * `levelSum` deixou de poder ser somado por cópias, portanto 6 000 bps é onde a
 * peça acaba - e o que está acima é recusado por todas as fronteiras que
 * escrevem um perfil, não cortado aqui.
 */
export const JEWEL_DAMAGE_REDUCTION_PER_LEVEL_BPS = 200;

/**
 * **O nível máximo de uma joia: 30, e é aqui que o número vive** (gate V28G.W).
 *
 * ## Porque ele mudou de casa
 *
 * Estava escrito em `ITEM_LEVEL_CAPS.jewel`, e `definitions.ts` importa este
 * ficheiro - portanto este não podia importar aquele para o ler. A consequência
 * era que qualquer teto de joia derivado do nível tinha de repetir o 30, ou o
 * produto dele, à mão.
 *
 * Mover a folha resolve-o sem inverter nada: `definitions.ts` continua a
 * declarar `ITEM_LEVEL_CAPS`, e `jewel-profile.ts` continua a derivar
 * `JEWEL_LEVEL_MAX` dali. A diferença é que agora os três leem o MESMO número,
 * e este ficheiro - onde vivem os coeficientes por nível - passa a poder
 * multiplicar por ele.
 *
 * ## O que ele NÃO é
 *
 * Não é um clamp e não autoriza dados corrompidos. Armazenamento, socket, API,
 * bilhete, instantânea e perfil recusam 31 como inválido em vez de o cortarem;
 * os `min` defensivos do resolvedor existem para que um valor que escape pague
 * o mesmo que 30, e não mais.
 */
export const JEWEL_MAX_LEVEL = 30;

/*
 * Ele sobe no ficheiro no gate V28G.W.1: os três tetos abaixo passam a
 * multiplicá-lo, e uma constante lida antes de ser declarada é um erro de
 * carregamento e não um aviso.
 */

/**
 * O teto absoluto da Égide Esmeralda: **60 %** do dano hostil recebido.
 *
 * `200 x 30` dá exatamente isto, portanto num perfil legal este número nunca
 * morde - ele é a mesma resposta pela segunda porta. Fica como DEFESA e não
 * como promessa, e a defesa que ele dá é a que interessa: com ela, o
 * multiplicador de dano recebido é `>= 4 000` bps por construção, e um payload
 * hostil nunca pode ser reduzido a zero.
 *
 * Um teto sobre uma percentagem do dano RECEBIDO é diferente de um teto sobre um
 * atributo concedido por um item, que o `CLAUDE.md` proíbe: nada aqui limita a
 * vida, a cadência ou o dano que a joia dá. O que este número limita é a FRAÇÃO
 * de um golpe que deixa de acontecer, e a fração é a própria coisa que a joia é.
 */
export const JEWEL_DAMAGE_REDUCTION_MAX_BPS =
  JEWEL_DAMAGE_REDUCTION_PER_LEVEL_BPS * JEWEL_MAX_LEVEL;

/**
 * **O Coração do Titã** (gate V28G.R).
 *
 * A joia da VIDA MÁXIMA. Entra pela mesma porta que a Chama, o Pulso, o Orbe
 * Vampírico e a Égide Esmeralda - um coeficiente e um teto DECLARADOS no
 * catálogo - e pela mesma razão: ela não sorteia nada.
 *
 * Não cura, não regenera, não cria escudo, não reduz dano, não tem chance, não
 * tem proc, não tem cooldown e não escolhe um instante. Está ativa enquanto a
 * peça estiver encaixada, e o que ela produz é uma PARCELA somada às
 * percentagens de vida máxima de um casco.
 *
 * O que ela mede é a terceira coisa que um casco pode ser: as três primeiras
 * dizem quanto ele entrega, a Égide diz quanto de um golpe lhe chega, e esta
 * diz quantos golpes ele aguenta.
 */
export const MAX_HEALTH_JEWEL_ID = 'jewel_max_health';

/**
 * **2 % de vida máxima por nível.** Decisão do dono.
 *
 * A tabela inteira do gate V28G.R sai desta constante e de
 * {@link JEWEL_LEVEL_MAX}, sem uma única linha escrita à mão:
 *
 * | nível | bónus |
 * | ---: | ---: |
 * | 0 | 0 % |
 * | 1 | 2 % |
 * | 5 | 10 % |
 * | 10 | 20 % |
 * | 15 | 30 % |
 * | 20 | 40 % |
 * | 25 | 50 % |
 * | 30 | **60 %** |
 * | 31+ | 60 % |
 *
 * ## Porque 200, o mesmo número da Égide Esmeralda
 *
 * Porque as duas compram SOBREVIVÊNCIA, e é deliberado que custem o mesmo por
 * nível. O que elas compram não é a mesma coisa: 60 % de vida faz um casco
 * durar 60 % mais tempo - é linear - e 60 % de redução faz durar duas vezes e
 * meia. A Égide vale mais por ponto, e paga por isso ao ser a única passiva
 * cujo valor depende de alguém estar a bater.
 *
 * As duas compõem sem regra especial nenhuma: nível 30 nas duas é `1,60 / 0,40`
 * = **quatro vezes** a vida efetiva da nave-base, e esse número sai das duas
 * fórmulas independentes em vez de uma linha escrita a decidi-lo.
 */
export const JEWEL_MAX_HEALTH_PER_LEVEL_BPS = 200;

/**
 * O teto absoluto do Coração do Titã: **+60 %** de vida máxima.
 *
 * `200 x 30` dá exatamente isto, portanto num perfil legal este número nunca
 * morde - é a mesma resposta pela segunda porta. Declarado por joia, como o do
 * Orbe Vampírico e o da Égide, e pela mesma razão: uma segunda joia de vida
 * teria o seu, e um teto partilhado seria uma decisão de equilíbrio tomada por
 * acidente no dia em que ela chegasse.
 *
 * ## Porque um teto AQUI não é o teto que o `CLAUDE.md` proíbe
 *
 * O que o projeto proíbe é capar o atributo que um item concede. Este número não
 * capa vida nenhuma: ele diz onde ESTA joia acaba de crescer, que é uma decisão
 * de conteúdo sobre uma peça, e não um tecto sobre a vida de uma nave. A vida
 * máxima de um casco continua sem limite - pontos, artefactos e equipamento
 * somam-se por cima sem que nada aqui os veja, e a escala do casco cresce com
 * eles.
 */
export const JEWEL_MAX_HEALTH_MAX_BPS = JEWEL_MAX_HEALTH_PER_LEVEL_BPS * JEWEL_MAX_LEVEL;

/** O id do Pulso do Dragão, nomeado uma vez (gate V28G.D). */
export const ATTACK_SPEED_JEWEL_ID = 'jewel_attack_speed';

/** O id do Orbe Vampírico, nomeado uma vez (gate V28G.I). */
export const LIFESTEAL_JEWEL_ID = 'jewel_lifesteal';

/**
 * +30 bps do DANO APLICADO devolvidos em vida, por nível somado.
 *
 * A mesma magnitude da Chama da Destruição e do Pulso do Dragão, e a paridade é
 * deliberada: o orçamento do V28D.2 é de 30 bps de poder de combate por nível, e
 * dano, cadência e sobrevivência são três maneiras de o gastar.
 *
 * ## Porque não precisa de compensar coisa nenhuma
 *
 * Uma joia de proc entrega `chance x magnitude`, portanto a magnitude tem de
 * subir quando a chance congela — senão o poder esperado deixa de ser linear e
 * o nível 1 entrega cinquenta e oito vezes menos do que promete (ver o V28G.F).
 *
 * Esta não sorteia. Entrega `bps/10000 x DPS` e mais nada, portanto a fração do
 * DPS que devolve em vida é `bps/100`, que é `0,30 % x levelSum` **por
 * construção**. O orçamento fecha com uma constante em vez de duas afinadas uma
 * contra a outra, e `docs/gates/v28gf-proc-math.mjs` verifica-o nível a nível.
 *
 * - nível 1 — `30` bps = **0,30 %** do dano aplicado;
 * - nível 30 — `900` bps = **9 %**, e é onde a joia para.
 *
 * Os degraus de 30 % e de 50 % que esta tabela mostrava ficavam em `levelSum`
 * 100 e 167 - alcançáveis só somando cópias, que o V28G.K aboliu. Desde o gate
 * V28G.W o teto de {@link JEWEL_LIFESTEAL_MAX_BPS} é DERIVADO deste coeficiente
 * e do nível máximo, portanto ele vale exatamente 900 bps e não pode voltar a
 * prometer um degrau que o nível não alcança.
 */
export const JEWEL_LIFESTEAL_PER_LEVEL_BPS = 30;

/**
 * O teto do Orbe Vampírico: **9 %** do dano aplicado, e é DERIVADO.
 *
 * ## A contradição que isto remove (gate V28G.W)
 *
 * Era `5000` - 50 % - escrito à mão. Esse número precisava de `levelSum` 167 e
 * desde o V28G.K nenhum casco legal lá chega: o nível para nos 30, que dão 900
 * bps. Ficava como "defesa", mas uma defesa que o outro corte torna inalcançável
 * não defende nada - **contradizia o contrato público**, que promete 9 % e mais
 * nada, e obrigava a frase do jogador a escolher qual dos dois tetos mostrar.
 *
 * ## Derivado, e não um 900 escrito à mão
 *
 * `coeficiente x nível máximo`. É a mesma aritmética que o resolvedor faz, e
 * mover qualquer um dos dois move este teto com eles - que é a única forma de
 * ele nunca voltar a discordar da tabela.
 *
 * ## Porque o campo CONTINUA a existir
 *
 * Porque o resolvedor aplica o teto **por joia e antes da soma**, com a mesma
 * forma nas quatro grandezas com teto, e recusa ao carregar uma joia de cura sem
 * ele declarado. Remover o campo faria a cura o caso especial das quatro e
 * apagaria uma guarda que existe para o dia em que uma segunda joia de cura
 * chegar - essa pode legitimamente querer um teto ABAIXO do produto.
 *
 * Um teto sobre uma percentagem do dano aplicado é diferente de um teto sobre um
 * atributo concedido por um item, que o `CLAUDE.md` proíbe: nada aqui limita a
 * cadência, o dano ou a vida que a joia dá. O que este número limita é a FRAÇÃO
 * de um golpe que volta como vida, e a fração é a própria coisa que a joia é.
 */
export const JEWEL_LIFESTEAL_MAX_BPS = JEWEL_LIFESTEAL_PER_LEVEL_BPS * JEWEL_MAX_LEVEL;

/**
 * O que o Pulso do Dragão vale por nível somado, em basis points de CADÊNCIA.
 *
 * Trinta pontos-base por nível, a mesma magnitude da Chama da Destruição e pela
 * mesma razão: a tabela do contrato V28D.2 dá às duas 900 bps de poder de
 * combate ao nível 30, porque dano e cadência são equivalentes para o DPS. A
 * paridade é uma decisão de desenho, não uma coincidência.
 *
 * - nível 1 — `30` bps = **+0,30 %**;
 * - nível 30 — `900` bps = **+9 %**, e é o teto final.
 *
 * Não há terceira linha. Uma skin tem UMA joia de cada tipo (gate V28G.K), o
 * nível para nos 30, e o `levelSum` 180 que esta tabela mostrava descrevia seis
 * cópias iguais - uma configuração de combate que deixou de poder existir.
 *
 * ## O que a percentagem significa exatamente
 *
 * Uma percentagem de TAXA, nunca de intervalo. `+9 %` quer dizer nove por cento
 * mais tiros por segundo, e o intervalo que produz isso é
 * `base x 10000 / (10000 + bps)` - não `base x 0,91`, que seria +9,89 %.
 *
 * ## Onde o arredondamento vive, e porque não é compensado
 *
 * No PvP o intervalo é um inteiro de milli-tiques e a divisão trunca, o que
 * encurta o intervalo e dá ao jogador até **+0,024 pontos percentuais** a mais
 * do que a etiqueta promete (o pior caso medido, quando `levelSum` 180 ainda era
 * possível; ao teto de hoje, 30, é menor). Está
 * aprovado como está: compensar com um tiro extra, um tiro suprimido, uma
 * alternância ou qualquer aleatoriedade seria trocar um desvio de vigésimo de
 * ponto percentual por uma cadência que deixa de ser determinística.
 *
 * O resíduo abaixo do milli-tique não se perde - o acumulador de
 * `cooldownMilliTicks` transporta-o para a salva seguinte.
 *
 * ## O que ele NÃO faz
 *
 * Não cria salvas, projéteis, temporizadores nem entidades. Não interage com a
 * runa de movimento, com a de Dano Dobrado, com procs nem com o dano. Aumenta
 * uma magnitude - a taxa - e mais nada.
 */
export const JEWEL_ATTACK_SPEED_PER_LEVEL_BPS = 30;

/** O id do Passo Alado, nomeado uma vez (gate V28G.T). */
export const MOVE_SPEED_JEWEL_ID = 'jewel_move_speed';

/**
 * O id do Coração Vital, nomeado uma vez (gate V28G.U).
 *
 * A joia da CURA DE SUPORTE. Como o Núcleo Atômico, o Arsenal Ígneo, o Orbe da
 * Víbora, a Garra Hemorrágica, a Égide Espinhosa e o Diamante Serrilhado, ela
 * não declara nada em {@link JewelEffects}: uma CHANCE não é um atributo somado,
 * e a dela vive no registo do seu próprio sorteio
 * (`game-domain/src/damage/vital-heart.ts`), ao lado da magnitude e da lei que
 * escolhe quem é curado.
 *
 * Um campo aqui seria uma segunda casa para a mesma decisão, e a que envelhece é
 * sempre a que ninguém lê.
 */
export const REGENERATION_JEWEL_ID = 'jewel_regeneration';

/**
 * **1,3 % de velocidade de movimento por nível.** Decisão do dono.
 *
 * A tabela inteira do gate V28G.T sai desta constante e de
 * {@link JEWEL_LEVEL_MAX}, sem uma única linha escrita à mão:
 *
 * | nível | bónus |
 * | ---: | ---: |
 * | 0 | 0 % |
 * | 1 | 1,3 % |
 * | 10 | 13 % |
 * | 20 | 26 % |
 * | 30 | **39 %** |
 * | 31+ | 39 % |
 *
 * ## Porque 130, e não os 30 das três primeiras nem os 200 das duas seguintes
 *
 * Porque o que ela compra não é poder de combate direto. Dano, cadência e cura
 * valem 30 bps por nível porque as três são o MESMO orçamento de DPS gasto de
 * três maneiras; vida e redução valem 200 porque compram sobrevivência - linear
 * numa, mais do que linear na outra.
 *
 * Movimento não é nenhuma das duas famílias: não aumenta o que um tiro vale nem
 * o que um casco aguenta. No duelo compra POSIÇÃO - chegar a uma pilastra antes
 * de um míssil, sair de um círculo de bomba, manter distância - e na caça leva a
 * nave pelo mesmo caminho mais depressa sem lhe dar um alvo a mais.
 *
 * ## O que a percentagem significa exatamente
 *
 * Uma percentagem do DESLOCAMENTO VOLUNTÁRIO, e de mais nada. `+39 %` quer
 * dizer trinta e nove por cento mais distância percorrida por segundo quando o
 * jogador manda a nave andar - ou, na caça, quando ela vagueia sozinha.
 *
 * Ela entra no agrupamento percentual do casco e é aplicada UMA vez. Um casco
 * com `+20 %` próprios e esta joia ao nível 30 anda a `1,59x`, e nunca a
 * `1,20 x 1,39 = 1,668x`: é a mesma lei aditiva do contrato V28D.2 que já
 * governa o dano, a cadência e a vida.
 *
 * ## O que ele NÃO faz
 *
 * Não toca em cadência, dano, projétil, míssil, bomba, serra, teleporte,
 * empurrão, reflexão, correção da autoridade, spawn, reconexão nem animação. Ele
 * multiplica um passo que o JOGADOR pediu, e mais nada - e é por isso que um
 * casco empurrado por uma explosão não recupera o controlo mais cedo por ter
 * esta joia.
 */
export const JEWEL_MOVE_SPEED_PER_LEVEL_BPS = 130;

/**
 * O teto absoluto do Passo Alado: **+39 %** de velocidade de movimento.
 *
 * `130 x 30` dá exatamente isto, portanto num perfil legal este número nunca
 * morde - é a mesma resposta pela segunda porta, e é deliberado que haja duas.
 * O contrato do gate V28G.T diz que 39 % é ABSOLUTO: nenhuma quantidade de
 * cópias, nenhum perfil adulterado, nenhuma instantânea antiga e nenhum valor
 * acima de 30 pode ultrapassá-lo. Um corte que dependesse só de
 * {@link JEWEL_LEVEL_MAX} deixaria de o garantir no dia em que esse teto se
 * movesse.
 *
 * ## Porque um teto AQUI não é o teto que o `CLAUDE.md` proíbe
 *
 * O que o projeto proíbe é capar o atributo que um item concede. Este número não
 * capa a velocidade de nave nenhuma: ele diz onde ESTA joia acaba de crescer,
 * que é uma decisão de conteúdo sobre uma peça. A velocidade de um casco continua
 * sem limite - o bónus próprio do casco e a runa de movimento somam e multiplicam
 * por cima sem que nada aqui os veja.
 */
export const JEWEL_MOVE_SPEED_MAX_BPS = JEWEL_MOVE_SPEED_PER_LEVEL_BPS * JEWEL_MAX_LEVEL;

/**
 * Quantos corações uma nave pode chegar a ter — o limite TÉCNICO, declarado.
 *
 * `numeric(40, 0)` guarda até `10^40 - 1`, a mesma capacidade exacta que as
 * moedas já têm (`CURRENCY_MAX_DIGITS`). É um limite de **armazenamento**, não
 * de jogo, e é verificado na aplicação antes da base, como as moedas: uma
 * incorporação que fizesse a contagem passar 40 dígitos é recusada com um erro
 * nomeado, nunca truncada e nunca aceite em silêncio.
 *
 * Não existe tecto de gameplay: não há saturação, não há multiplicador que
 * congele, e não há ponto em que um coração deixe de valer +1%. A fronteira
 * existe porque toda a representação real tem uma.
 */
export const PRIMORDIAL_HEART_MAX_COUNT = CURRENCY_MAX_MAGNITUDE;

/** Se esta contagem cabe no que a base guarda. Exacta, sempre em `bigint`. */
export function isPrimordialHeartCountWithinCapacity(count: bigint): boolean {
  return count >= 0n && count <= PRIMORDIAL_HEART_MAX_COUNT;
}

/** Bumped quando o SIGNIFICADO deste catálogo mudar. */
export const JEWEL_CONTENT_VERSION = 1;

/*
 * Este número FICOU em 1 no gate V28G.B, e a decisão está aqui em vez de ser um
 * esquecimento.
 *
 * Ligar a Chama da Destruição é uma mudança de conteúdo, e o instinto foi subir
 * para 2. A suíte do cofre apanhou a consequência: `JEWEL_CONTENT_VERSION`
 * alimenta o `content_version` que a projeção `item_definitions` guarda, e essa
 * tabela é semeada por MIGRAÇÃO - portanto subir o número aqui e não escrever a
 * migração deixa o código e o banco a discordarem, que é exatamente o que
 * `inventory.test.ts` existe para apanhar.
 *
 * Não subiu porque não precisa de subir para nada estar certo. Nenhuma sessão e
 * nenhum perfil congelam o `contentVersion` de uma joia - o perfil agregado
 * carrega `id`, `copies` e `levelSum` e mais nada - por isso não há registo
 * antigo cuja leitura este número decida. O que separa uma corrida com joias de
 * uma sem é a impressão digital do loadout, que já carrega o perfil.
 *
 * O gate que escrever a migração da projeção sobe-o então, junto com ela.
 */

/**
 * Os nomes que um jogador lê, decididos pelo dono (gate V28C).
 *
 * Aqui e não em `display-names.ts` porque um nome é CONTEÚDO: pertence à
 * definição, como o de um pet ou o de uma skin já pertence. A camada de
 * apresentação lê daqui, e é por isso que ela não tem de saber que joias
 * existem.
 *
 * Um nome não diz o que a joia faz. As catorze continuam sem efeito nenhum
 * neste gate, e ter nome não muda isso - nomear é o que permite mostrá-las sem
 * que ninguém leia um id.
 */
const SOCKETED_NAMES: Readonly<Record<string, string>> = Object.freeze({
  jewel_saw: 'Diamante Serrilhado',
  jewel_missile: 'Arsenal Ígneo',
  jewel_lifesteal: 'Orbe Vampírico',
  jewel_damage_reflection: 'Égide Espinhosa',
  jewel_blade_cut: 'Ciclone de Lâminas',
  jewel_atomic_bomb: 'Núcleo Atômico',
  jewel_bleeding: 'Garra Hemorrágica',
  jewel_poison: 'Orbe da Víbora',
  jewel_max_health: 'Coração do Titã',
  jewel_damage: 'Chama da Destruição',
  jewel_damage_reduction: 'Égide Esmeralda',
  jewel_move_speed: 'Passo Alado',
  jewel_regeneration: 'Coração Vital',
  jewel_attack_speed: 'Pulso do Dragão',
});

/**
 * As catorze esferas, pela ordem dos seus masters.
 *
 * A raridade é `legendary` em todas por agora e é uma etiqueta de catálogo, não
 * uma decisão de conteúdo: nenhuma delas é obtenível, e a distribuição por
 * raridade faz parte do desenho que ainda não existe.
 */
const SOCKETED_MASTERS = [
  'jewel_atomic_bomb',
  'jewel_attack_speed',
  'jewel_blade_cut',
  'jewel_bleeding',
  'jewel_damage',
  'jewel_damage_reduction',
  'jewel_damage_reflection',
  'jewel_lifesteal',
  'jewel_max_health',
  'jewel_missile',
  'jewel_move_speed',
  'jewel_poison',
  'jewel_regeneration',
  'jewel_saw',
] as const;

/** Nove das catorze ainda não têm conteúdo decidido, e o vazio diz isso. */
const EMPTY_EFFECTS: JewelEffects = Object.freeze({});

/**
 * O que cada joia de socket concede hoje, por id.
 *
 * Uma tabela e não uma cadeia de ternários: com catorze joias a serem ligadas
 * uma de cada vez, um `id === A ? x : id === B ? y : {}` cresce até ninguém
 * conseguir ler qual delas tem o quê.
 *
 * **Uma ausência aqui NÃO diz que a joia não tem mecânica** (gate V28G.W.1).
 *
 * Diz só que ela não concede uma magnitude LINEAR POR NÍVEL que
 * `resolveJewelEffects` some. Oito das catorze estão neste caso, e as oito têm
 * mecânica registada noutro sítio - numa loteria, num subsistema de estado, numa
 * órbita, numa cura. Uma bomba atómica não cabe na forma `coef x nível`, e não
 * caber aqui não a torna menos real.
 *
 * A auditoria V28G.W leu esta ausência como ausência de mecânica e escreveu-o
 * num relatório. `damage/jewel-mechanics.test.ts` nomeia as catorze e prova
 * onde cada uma está registada, exatamente para essa inferência não se repetir.
 */
const SOCKETED_EFFECTS: Readonly<Record<string, JewelEffects>> = Object.freeze({
  [DAMAGE_JEWEL_ID]: Object.freeze({ damagePerLevelSumBps: JEWEL_DAMAGE_PER_LEVEL_BPS }),
  [ATTACK_SPEED_JEWEL_ID]: Object.freeze({
    attackSpeedPerLevelSumBps: JEWEL_ATTACK_SPEED_PER_LEVEL_BPS,
  }),
  [LIFESTEAL_JEWEL_ID]: Object.freeze({
    lifestealPerLevelSumBps: JEWEL_LIFESTEAL_PER_LEVEL_BPS,
    lifestealMaxBps: JEWEL_LIFESTEAL_MAX_BPS,
  }),
  /*
   * A Égide Esmeralda entra pela mesma porta que as outras três (gate V28G.Q).
   *
   * Um coeficiente e um teto no CATÁLOGO, e nada num registo de loteria - porque
   * ela não sorteia. É a quarta passiva puramente numérica, e é a primeira cujo
   * número descreve o golpe de outra pessoa.
   */
  [DAMAGE_REDUCTION_JEWEL_ID]: Object.freeze({
    damageReductionPerLevelSumBps: JEWEL_DAMAGE_REDUCTION_PER_LEVEL_BPS,
    damageReductionMaxBps: JEWEL_DAMAGE_REDUCTION_MAX_BPS,
  }),
  /*
   * O Coração do Titã entra pela mesma porta que as outras quatro (gate V28G.R).
   *
   * Um coeficiente e um teto no CATÁLOGO, e nada num registo de loteria - porque
   * ele não sorteia. É a quinta passiva puramente numérica, e a primeira cujo
   * número descreve o que o casco AGUENTA em vez do que ele entrega ou recebe.
   */
  [MAX_HEALTH_JEWEL_ID]: Object.freeze({
    maxHealthPerLevelSumBps: JEWEL_MAX_HEALTH_PER_LEVEL_BPS,
    maxHealthMaxBps: JEWEL_MAX_HEALTH_MAX_BPS,
  }),
  /*
   * O Passo Alado entra pela mesma porta que as outras cinco (gate V28G.T).
   *
   * Um coeficiente e um teto no CATÁLOGO, e nada num registo de loteria - porque
   * ele não sorteia. É a sexta passiva puramente numérica, e a primeira cujo
   * número não descreve o que o casco entrega, nem o que ele recebe, nem o que
   * ele aguenta: descreve onde ele consegue estar.
   */
  [MOVE_SPEED_JEWEL_ID]: Object.freeze({
    moveSpeedPerLevelSumBps: JEWEL_MOVE_SPEED_PER_LEVEL_BPS,
    moveSpeedMaxBps: JEWEL_MOVE_SPEED_MAX_BPS,
  }),
});

/** Os dois modos, congelada e partilhada: a maioria das joias vale nos dois. */
const BOTH_MODES: readonly JewelEffectMode[] = Object.freeze(['hunt', 'pvp']);

/** Só o duelo. Uma joia, hoje, e a razão está na sua linha. */
const PVP_ONLY: readonly JewelEffectMode[] = Object.freeze(['pvp']);

/** Nenhum. Duas joias cuja mecânica não existe em build nenhum. */
const NO_MODES: readonly JewelEffectMode[] = Object.freeze([]);

/**
 * **Onde cada joia de socket produz efeito.** Lida do código, não da prosa.
 *
 * Uma tabela e não um ternário, pela mesma razão que {@link SOCKETED_EFFECTS} é
 * uma: com catorze joias a serem ligadas uma de cada vez, uma cadeia cresce até
 * ninguém conseguir ler qual delas vale onde.
 *
 * ## As três respostas, e o que cada uma quer dizer
 *
 * - **os dois modos** - onze das catorze. O caminho que produz o efeito é
 *   importado pelas DUAS simulações: `resolveJewelEffects` para a Chama, o Pulso
 *   e o Orbe Vampírico; `proc-lottery` para o Ciclone; `volley-lottery` para o
 *   Núcleo e o Arsenal; e os ficheiros próprios do Orbe da Víbora e da Garra;
 *
 * - **só o duelo** - a Égide Espinhosa, e só ela. `damage/reflection.ts` é
 *   importado por `pvp/simulation.ts` e por mais nada: `hunt/simulation.ts` não
 *   o nomeia, não tem fluxo defensivo, e a caça não tem payload hostil nenhum
 *   para intercetar. Ver o ADR 0058 e `docs/gates/v28go-reflection-contract.md`;
 *
 * - **nenhum modo** - duas cujas mecânicas não estão desenhadas. Elas não
 *   declaram efeito no catálogo, não estão em registo de loteria nenhum, e não
 *   têm ficheiro próprio. `[]` é o estado real delas, e é deliberadamente
 *   diferente de "só o duelo": anunciar exclusividade de duelo a uma peça que
 *   também não faz nada no duelo seria uma promessa.
 */
const SOCKETED_MODES: Readonly<Record<string, readonly JewelEffectMode[]>> = Object.freeze({
  [DAMAGE_JEWEL_ID]: BOTH_MODES,
  [ATTACK_SPEED_JEWEL_ID]: BOTH_MODES,
  [LIFESTEAL_JEWEL_ID]: BOTH_MODES,
  [BLADE_CUT_JEWEL_ID]: BOTH_MODES,
  [ATOMIC_BOMB_JEWEL_ID]: BOTH_MODES,
  [MISSILE_JEWEL_ID]: BOTH_MODES,
  [POISON_JEWEL_ID]: BOTH_MODES,
  [BLEEDING_JEWEL_ID]: BOTH_MODES,
  /*
   * **O Diamante Serrilhado, nos dois modos** (gate V28G.P).
   *
   * `damage/saw.ts` é importado por `pvp/simulation.ts` E por
   * `hunt/simulation.ts`, e o que ele produz é a mesma lei nos dois: a mesma
   * contagem, o mesmo multiplicador, o mesmo intervalo e o mesmo orçamento. O
   * que muda entre eles é só o vocabulário do alvo - um adversário no duelo, um
   * mob ou um boss na caça - e a unidade do relógio.
   *
   * Ao contrário da Égide, ela não tem nada de defensivo: não precisa de um
   * autor a quem devolver, portanto a razão que torna a Reflexão exclusiva do
   * duelo não lhe toca.
   */
  [SAW_JEWEL_ID]: BOTH_MODES,
  /*
   * **A Égide Esmeralda, nos dois modos** (gate V28G.Q).
   *
   * `damage/damage-reduction.ts` é importado por `pvp/simulation.ts` E por
   * `hunt/simulation.ts`, e o que ele aplica é a mesma função nos dois: o mesmo
   * multiplicador, a mesma truncatura, a mesma ordem em relação ao clamp da
   * vida. O que muda entre eles é só a unidade em que a vida do casco está - um
   * inteiro no duelo, um `number` fracionário na caça - e as duas fronteiras
   * atravessam para a MESMA aritmética inteira.
   *
   * Ao contrário da Égide Espinhosa, ela não precisa de um autor a quem
   * devolver: reduzir um golpe é uma coisa que se faz ao golpe, e não a quem o
   * deu. A razão que torna a Reflexão exclusiva do duelo não lhe toca, e a caça
   * TEM dano hostil - a fuga de um mob - para ela reduzir.
   */
  [DAMAGE_REDUCTION_JEWEL_ID]: BOTH_MODES,
  /*
   * **A Égide Espinhosa, e a decisão está fechada: exclusivamente PvP.**
   *
   * Não é uma lacuna à espera de ser preenchida. A caça tem uma única fonte de
   * dano à nave - um mob que atravessa a fronteira de baixo - e esse mob é
   * removido no MESMO tique em que causa o dano; a passagem do boss é morte
   * incondicional e não é dano. Não sobra autor a quem devolver, e uma reflexão
   * sem autor não é uma reflexão.
   *
   * A D-4.4 do V28D.2 propunha o "inimigo vivo mais avançado" como alvo
   * substituto. Ficou **superseded** e não será implementada: devolver um golpe
   * a quem não o deu não é o que esta joia é.
   */
  /*
   * **O Coração do Titã, nos dois modos** (gate V28G.R).
   *
   * `hull/max-health.ts` é importado por `pvp/combat-profile.ts` E por
   * `hunt/simulation.ts`, e o que ele compõe é a mesma lei nos dois: as mesmas
   * fontes planas somadas primeiro, as mesmas percentagens somadas depois, e uma
   * única multiplicação no fim. O que muda entre eles é só a unidade em que a
   * vida do casco está - um inteiro no duelo, um `number` fracionário na caça - e
   * as duas fronteiras atravessam para a MESMA aritmética inteira.
   *
   * Como a Égide Esmeralda, ele não precisa de um autor: aumentar o teto de um
   * casco é uma coisa que se faz ao casco, e a caça tem um casco tal como o
   * duelo tem.
   */
  [MAX_HEALTH_JEWEL_ID]: BOTH_MODES,
  /*
   * **O Passo Alado, nos dois modos** (gate V28G.T).
   *
   * As duas simulações têm uma nave que se desloca por vontade própria, e o
   * coeficiente que as duas aplicam é o mesmo `bigint` do mesmo resolvedor,
   * somado ao mesmo agrupamento percentual e aplicado uma única vez.
   *
   * O que muda entre elas é só a FORMA do deslocamento voluntário. No duelo é um
   * passo por tique numa direção que o jogador segura, e a percentagem multiplica
   * esse passo; na caça a nave vagueia por uma função do tique e da semente, e a
   * percentagem acelera o relógio dessa função. Nos dois casos a distância
   * percorrida por segundo sobe exatamente pelo mesmo coeficiente - ver o ADR
   * 0062.
   *
   * Como a Égide Esmeralda e o Coração do Titã, ele não precisa de um autor:
   * andar mais depressa é uma coisa que se faz ao casco.
   */
  [MOVE_SPEED_JEWEL_ID]: BOTH_MODES,
  /*
   * **O Coração Vital, nos dois modos** (gate V28G.U).
   *
   * `damage/vital-heart.ts` é importado por `pvp/simulation.ts` E por
   * `hunt/simulation.ts`, e o que ele produz é a mesma lei nos dois: a mesma
   * chance por salva, o mesmo teto de 6 %, a mesma magnitude de 150 % do
   * dano-base, a mesma seleção de alvo e a mesma proibição de overheal.
   *
   * O que muda entre eles é quantos ALIADOS existem para escolher. Na caça há uma
   * nave só, portanto a lei escolhe sempre a própria - e num duelo 1x1 também,
   * pela mesma lei e sem um ramo próprio. É no 3x3 que ela cura outra pessoa, e
   * isso é a mesma função a responder com mais candidatos, não um segundo
   * comportamento.
   *
   * Como o Coração do Titã e o Passo Alado, ela não precisa de um autor a quem
   * devolver: curar um casco é uma coisa que se faz ao casco.
   */
  [REGENERATION_JEWEL_ID]: BOTH_MODES,
  [DAMAGE_REFLECTION_JEWEL_ID]: PVP_ONLY,
});

export const JEWEL_DEFINITIONS: readonly JewelDefinition[] = Object.freeze([
  ...SOCKETED_MASTERS.map(
    (definitionId): JewelDefinition =>
      Object.freeze({
        definitionId,
        socketMode: 'socketed' as const,
        visualShape: 'orb' as const,
        master: `${definitionId}.png`,
        displayName: SOCKETED_NAMES[definitionId] ?? null,
        /*
         * Cinco das catorze concedem, e nove continuam vazias.
         *
         * O vazio continua a ser o estado real do conteudo delas: um campo
         * opcional ausente diz "ainda nao decidido", e um zero diria "decidido
         * que e nada" - que e diferente e falso.
         */
        effects: SOCKETED_EFFECTS[definitionId] ?? EMPTY_EFFECTS,
        /*
         * Onde ela FUNCIONA, e a ausência é uma resposta.
         *
         * Uma joia fora de `SOCKETED_MODES` não produz efeito em modo nenhum -
         * é o estado das duas cujas mecânicas não estão desenhadas - e `[]` diz
         * isso, em vez de deixar o campo por preencher.
         */
        supportedModes: SOCKETED_MODES[definitionId] ?? NO_MODES,
        rarity: 'legendary' as Rarity,
      }),
  ),
  Object.freeze({
    definitionId: SOVEREIGN_JEWEL_ID,
    socketMode: 'sovereign' as const,
    visualShape: 'silhouette' as const,
    master: `${SOVEREIGN_JEWEL_ID}.png`,
    displayName: 'Coração Primordial',
    effects: Object.freeze({ damageBps: SOVEREIGN_JEWEL_DAMAGE_BPS }),
    /*
     * Os dois modos. `primordialHeartBps` é importado por `pvp/combat-profile.ts`
     * e por `hunt/simulation.ts`, e o que ele soma é a mesma parcela nos dois.
     */
    supportedModes: BOTH_MODES,
    rarity: 'prismatic' as Rarity,
  }),
]);

const BY_ID: ReadonlyMap<string, JewelDefinition> = new Map(
  JEWEL_DEFINITIONS.map((jewel) => [jewel.definitionId, jewel]),
);

export function jewelDefinition(definitionId: string): JewelDefinition | null {
  return BY_ID.get(definitionId) ?? null;
}

export function isJewelDefinitionId(definitionId: string): boolean {
  return BY_ID.has(definitionId);
}

/**
 * Se esta joia pode entrar num socket.
 *
 * A pergunta que o serviço e o trigger fazem, e a única forma de a fazer: pelo
 * MODO, nunca por comparar o id com o da soberana. Um id comparado à mão é uma
 * regra que a décima sexta joia esquece.
 */
export function jewelFitsSocket(definitionId: string): boolean {
  return BY_ID.get(definitionId)?.socketMode === 'socketed';
}

export function isSovereignJewel(definitionId: string): boolean {
  return BY_ID.get(definitionId)?.socketMode === 'sovereign';
}

/**
 * Onde esta joia produz efeito. Vazio para uma que não produz em lado nenhum.
 *
 * A única forma de a pergunta ser feita, e é por isso que ela devolve a lista em
 * vez de um booleano: um chamador que queira saber "vale na caça" pergunta
 * {@link jewelWorksIn}, e um que queira mostrar a lista tem-na.
 *
 * Um id desconhecido devolve vazio, e não atira: a interface desenha o que o
 * servidor lhe deu, e uma peça que este build não conhece não deve derrubar um
 * painel de inventário.
 */
export function jewelSupportedModes(definitionId: string): readonly JewelEffectMode[] {
  return BY_ID.get(definitionId)?.supportedModes ?? NO_MODES;
}

/** Se esta joia produz efeito NESTE modo. */
export function jewelWorksIn(definitionId: string, mode: JewelEffectMode): boolean {
  return jewelSupportedModes(definitionId).includes(mode);
}

/**
 * **Se esta joia é exclusivamente de DUELO**: vale no PvP e não vale na caça.
 *
 * ## Porque as duas metades da pergunta são feitas
 *
 * Porque `!jewelWorksIn(id, 'hunt')` sozinho seria verdadeiro também para as
 * quatro joias que não produzem efeito em modo nenhum - e anunciar-lhes
 * exclusividade de duelo seria prometer um efeito que elas não têm em lado
 * nenhum. A exclusividade exige que o efeito EXISTA algures.
 *
 * É esta função, e nenhuma lista de ids, que decide se a interface mostra o
 * aviso. Uma lista escrita num componente seria uma segunda casa para uma
 * decisão que o catálogo já tem - e a que envelhece é sempre a que ninguém lê.
 */
export function isPvpOnlyJewel(definitionId: string): boolean {
  return jewelWorksIn(definitionId, 'pvp') && !jewelWorksIn(definitionId, 'hunt');
}

/** Os ids soberanos, para o banco desenhar o seu índice único parcial. */
export const SOVEREIGN_JEWEL_IDS: readonly string[] = Object.freeze(
  JEWEL_DEFINITIONS.filter((jewel) => jewel.socketMode === 'sovereign').map(
    (jewel) => jewel.definitionId,
  ),
);
